// @erp/module-cms/schemas — THE ONE URL GRAMMAR for anything the public site puts in an
// href or a src (SEC-5 of the ADR-010 review, 2026-09-13). Two things leave the ERP as
// bare URLs the site renders verbatim: an editor-typed `link` on an announcement (a card's
// href, relative routes allowed), and the `url` of every attachments[] entry and every
// event banner (an <a href> / <img src>, absolute only). The body_html sanitiser
// (api/sanitize.ts) already refused `javascript:` and its obfuscations INSIDE the body —
// but these fields never passed through it, so `{type:'pdf', url:'javascript:…'}` reached
// the site's download CTA untouched. Both lanes parse what they exchange, so the grammar
// lives here in schemas (the ground the api and fe lanes share, ADR-001 Fig 4.1) and the
// sanitiser imports it rather than carrying a second copy that could drift.
//
// Two shapes, deliberately:
//   SafeHref  — a relative reference (`/events/x`, `#top`, `?q=1`, `//host/x`) or an
//               absolute URL under http, https, mailto or tel. Judged on the value AFTER
//               the browser's own normalisation (entities decoded twice, the characters a
//               URL parser ignores while looking for a scheme stripped, lower-cased), so
//               `&#106;avascript:`, `java\tscript:`, `JAVASCRIPT:` and the C1-remapped
//               numeric forms are all seen as the scheme they are.
//   HttpUrl   — an absolute http(s) URL, as `new URL()` parses it (Zod 4's z.url with a
//               protocol rule): what an <img src> and a download link need. A relative
//               attachment URL has no meaning on a static site served from another host.
import { z } from "zod";

/** The schemes a kept href/src may carry (a scheme-less value is a relative reference and is fine). */
export const SAFE_SCHEMES: ReadonlySet<string> = new Set(["http", "https", "mailto", "tel"]);

/**
 * The named entities an attacker can use to hide a scheme's characters. Numeric forms
 * (&#106; / &#x6A; / with or without the semicolon, leading zeros) are decoded
 * generically below; only these names matter for obfuscating `javascript:`.
 */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  tab: "\t", newline: "\n", colon: ":", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'",
  nbsp: "\u00a0", sol: "/", num: "#", semi: ";",
};

/**
 * The HTML spec's numeric-character-reference table for 0x80–0x9F: a browser maps these
 * to the Windows-1252 characters, not to the C1 controls the number names.
 */
const C1_REMAP: Readonly<Record<number, number>> = {
  0x80: 0x20ac, 0x82: 0x201a, 0x83: 0x0192, 0x84: 0x201e, 0x85: 0x2026, 0x86: 0x2020, 0x87: 0x2021,
  0x88: 0x02c6, 0x89: 0x2030, 0x8a: 0x0160, 0x8b: 0x2039, 0x8c: 0x0152, 0x8e: 0x017d, 0x91: 0x2018,
  0x92: 0x2019, 0x93: 0x201c, 0x94: 0x201d, 0x95: 0x2022, 0x96: 0x2013, 0x97: 0x2014, 0x98: 0x02dc,
  0x99: 0x2122, 0x9a: 0x0161, 0x9b: 0x203a, 0x9c: 0x0153, 0x9e: 0x017e, 0x9f: 0x0178,
};

/**
 * A numeric reference's code point → the character a browser produces (the spec's
 * "numeric character reference end state"): 0, anything past U+10FFFF (including a digit
 * run too long to be a number at all) and a surrogate become U+FFFD; 0x80–0x9F are
 * remapped; everything else is the code point itself.
 */
const numericRef = (cp: number): string => {
  if (!Number.isFinite(cp) || cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return "\ufffd";
  return String.fromCodePoint(C1_REMAP[cp] ?? cp);
};

/**
 * One entity-decoding pass (the browser's own decoding, approximated for the characters a
 * scheme can hide in). Numeric forms consume EVERY digit, as the spec does — a browser
 * turns `&#x00000006A;` into "j" no matter how many leading zeros there are, so a cap on
 * the digit count here was a bypass (nine hex digits, eleven decimal; found 2026-09-13).
 * The semicolon is optional in both branches.
 */
const decodeEntities = (s: string): string =>
  s.replace(/&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z]{1,10});?/g, (whole, body: string) => {
    if (body.startsWith("#")) {
      const hex = body[1] === "x" || body[1] === "X";
      return numericRef(hex ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10));
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });

/**
 * Everything a browser ignores while it looks for a scheme: C0 controls + space, DEL +
 * C1 controls, soft hyphen, the zero-width family, line/paragraph separators, the
 * general-punctuation format characters and the BOM.
 */
const IGNORED_IN_SCHEME = /[\u0000-\u0020\u007f-\u009f\u00ad\u200b-\u200f\u2028-\u202f\u2060-\u206f\ufeff]/g;

const SCHEME = /^([a-z][a-z0-9+.-]*):/;

/** The scheme a browser would read from a URL value (lower-case), or null for a relative reference. */
export const urlScheme = (raw: string): string | null => {
  const v = decodeEntities(decodeEntities(raw)).replace(IGNORED_IN_SCHEME, "").toLowerCase();
  const m = SCHEME.exec(v);
  return m === null ? null : (m[1] as string);
};

/**
 * True when a URL attribute value is safe to keep: no scheme (relative), or one of
 * SAFE_SCHEMES. Decoded twice (a double-encoded `&amp;#106;` collapses in two passes),
 * control-stripped and lower-cased BEFORE the scheme is read — the browser does the
 * same normalisation before it navigates, so the check sees what the browser sees.
 */
export const isSafeUrl = (raw: string): boolean => {
  const scheme = urlScheme(raw);
  return scheme === null ? true : SAFE_SCHEMES.has(scheme);
};

export const HREF_MAX = 2000;

/** A card/notification link: a relative route, or an absolute http(s)/mailto/tel URL. Never javascript:, data:, vbscript:, file:. */
export const SafeHref = z
  .string()
  .trim()
  .max(HREF_MAX)
  .refine(isSafeUrl, { message: "Link must be a relative path or an http(s), mailto: or tel: URL" });

/** An absolute http(s) URL — what an <img src>, a banner or a downloadable attachment needs on the public site. */
export const HttpUrl = z.url({ protocol: /^https?$/, error: "Must be an absolute http(s) URL" }).trim().max(HREF_MAX);
